/**
 * What a job made after labor, card fees and supplies — the arithmetic only.
 *
 * Pure and shared, the same way the pricing engine is: the Worker computes
 * the snapshot a completed visit keeps, and the browser runs the estimate on
 * the quote screen as the quote is edited. Money is integer cents, shares are
 * basis points (10,000 = 100%) and time is minutes.
 *
 * The definitions, once:
 *
 *   * **Revenue** — the visit's price after discounts, less refunds, without
 *     sales tax (passed through) or tips (the crew's, never revenue or cost).
 *     A visit's stored price already includes tax when the quote it came from
 *     charged tax, so the tax comes back out at the quote's own share of it.
 *   * **Labor** — each worker separately, summed: clocked hours × an hourly
 *     rate, a flat amount per job, or a share of the price. A salary is costed
 *     per hour at salary ÷ the hours it covers and is only ever approximate.
 *     A W-2 worker's pay carries the payroll load on top; a 1099 contractor's
 *     does not. The owner's own time is free unless the company says
 *     otherwise. Unpaid time (a trainee riding along) costs nothing.
 *   * **Card fees** — what card payments cost the company, less any surcharge
 *     the customer paid on top, so a fee passed on nets out. Cash and checks
 *     cost nothing.
 *   * **Supplies** — an optional share of the price.
 *   * **Profit** — revenue less all of that. **Margin** — profit ÷ revenue.
 *
 * Informational, not tax or legal advice: the app costs the work the way an
 * owner describes it and never decides how anybody must be paid.
 */

import {
  BASIS_POINTS_SCALE,
  DEFAULT_SALARY_HOURS_PER_YEAR,
  type PayType,
  type PaymentMethodKind,
  type WorkerType,
} from '../constants/index.ts';
import { roundHalfUp } from './pricing.ts';

// ---------------------------------------------------------------------------
// Pay rates
// ---------------------------------------------------------------------------

/** One effective-dated rate. Which amount field is read depends on `payType`. */
export interface PayRate {
  payType: PayType;
  /** hourly: cents per hour. per_job: cents per completed visit. */
  rateCents: number | null;
  /** percent: the share of the job price. */
  rateBasisPoints: number | null;
  /** salary: the annual amount. */
  annualSalaryCents: number | null;
  /** salary: the hours a year it covers; null reads as 2,080. */
  salaryHoursPerYear: number | null;
  /** The first company-local date it applies to, YYYY-MM-DD. */
  effectiveFrom: string;
}

/**
 * The rate in force on a date: the latest one whose effective date is on or
 * before it. Null when the date is before every rate — a job done before the
 * person's pay was recorded is not costed at a rate nobody had agreed yet.
 *
 * ISO dates compare correctly as strings, so no date parsing happens here.
 */
export function payRateInEffect<T extends Pick<PayRate, 'effectiveFrom'>>(rates: readonly T[], date: string): T | null {
  let found: T | null = null;
  for (const rate of rates) {
    if (rate.effectiveFrom <= date && (found === null || rate.effectiveFrom > found.effectiveFrom)) found = rate;
  }
  return found;
}

/** A salary's hourly equivalent: the annual amount over the hours it covers. */
export function salaryHourlyCents(annualSalaryCents: number, hoursPerYear: number | null): number {
  const hours = hoursPerYear !== null && hoursPerYear > 0 ? hoursPerYear : DEFAULT_SALARY_HOURS_PER_YEAR;
  return roundHalfUp(annualSalaryCents / hours);
}

/** `amount × basisPoints`, rounded half away from zero. */
export function applyBasisPoints(amountCents: number, basisPoints: number): number {
  return roundHalfUp((amountCents * basisPoints) / BASIS_POINTS_SCALE);
}

/** Cents for `minutes` at an hourly figure. */
export function centsForMinutes(minutes: number, hourlyCents: number): number {
  return roundHalfUp((minutes * hourlyCents) / 60);
}

/**
 * One person's loaded hourly wage: the hourly rate, or a salary divided by
 * the hours it covers (2,080 when none are named), plus the W-2 payroll load.
 * A 1099 contractor's wage is not loaded. Percent and per-job pay have no
 * hourly figure, so they return null and stay out of an average.
 */
export function loadedHourlyCents(
  rate: PayRate,
  workerType: WorkerType | null,
  burdenBasisPoints: number,
): number | null {
  let hourly: number | null = null;
  if (rate.payType === 'hourly') hourly = rate.rateCents;
  else if (rate.payType === 'salary' && rate.annualSalaryCents !== null) {
    hourly = salaryHourlyCents(rate.annualSalaryCents, rate.salaryHoursPerYear);
  }
  if (hourly === null) return null;
  const burden = workerType === 'w2' ? applyBasisPoints(hourly, Math.max(0, burdenBasisPoints)) : 0;
  return hourly + burden;
}

/** One hourly or salaried person, already loaded, and the minutes they worked. */
export interface CrewHourlySample {
  loadedHourlyCents: number;
  minutes: number;
}

/**
 * The hourly backup suggested from Team pay: an hours-weighted average of
 * loaded hourly and salary rates. When nobody has worked yet, each person
 * counts once so the hint still has a number. Null when there is nobody to
 * average. Percent and per-job pay are not samples.
 */
export function suggestedCrewHourlyCents(samples: readonly CrewHourlySample[]): number | null {
  if (samples.length === 0) return null;
  const minutes = samples.reduce((sum, sample) => sum + Math.max(0, sample.minutes), 0);
  if (minutes <= 0) {
    return roundHalfUp(samples.reduce((sum, sample) => sum + sample.loadedHourlyCents, 0) / samples.length);
  }
  const weighted = samples.reduce((sum, sample) => sum + sample.loadedHourlyCents * Math.max(0, sample.minutes), 0);
  return roundHalfUp(weighted / minutes);
}

/**
 * What Profit by place charges for one visit's crew.
 *
 * `lines` null means the company has no Team pay: the whole visit is the
 * hourly backup times its person-minutes. With Team pay, each costed line is
 * wages plus the W-2 payroll load (the same sum Job profit calls loaded labor
 * cost). Owner time the company has not chosen to count, and unpaid time,
 * add nothing. A person with no rate, and a visit with nobody named on it,
 * use the hourly backup for those minutes. Null when some of those minutes
 * have no backup either: the report then shows hours and declines a margin.
 */
export function placeVisitLaborCost(input: {
  laborMinutes: number;
  lines: readonly WorkerCostLine[] | null;
  fallbackHourlyCents: number | null;
}): number | null {
  const atBackup = (minutes: number): number | null => {
    if (minutes <= 0) return 0;
    if (input.fallbackHourlyCents === null) return null;
    return centsForMinutes(minutes, input.fallbackHourlyCents);
  };

  const lines = input.lines;
  if (lines === null || lines.length === 0) return atBackup(Math.max(0, input.laborMinutes));

  let cost = 0;
  let missingMinutes = 0;
  for (const line of lines) {
    if (line.status === 'costed') cost += line.baseCents + line.burdenCents;
    else if (line.status === 'pay_missing') missingMinutes += Math.max(0, line.minutes);
  }
  if (missingMinutes > 0) {
    const backup = atBackup(missingMinutes);
    if (backup === null) return null;
    cost += backup;
  }
  return cost;
}

// ---------------------------------------------------------------------------
// Workers
// ---------------------------------------------------------------------------

/** Where a worker's minutes came from. */
export type MinutesSource =
  /** Their own clock punches. */
  | 'clock'
  /** The visit's minutes as somebody typed them at completion. */
  | 'typed'
  /** Nothing recorded — the visit's scheduled length stands in. */
  | 'scheduled';

/** One person's pay terms, independent of any visit. */
export interface WorkerPayTerms {
  userId: string;
  name: string;
  /** Owners are costed by the company's owner setting, never by their own rates. */
  isOwner: boolean;
  workerType: WorkerType | null;
  /**
   * The payroll load to apply when they are W-2: their own override, else
   * the company default, else nothing. Ignored for 1099 contractors.
   */
  burdenBasisPoints: number;
  /** The rate in force on the date being costed. */
  rate: PayRate | null;
}

/** One person's part in one visit. */
export interface WorkerOnVisit extends WorkerPayTerms {
  /** Paid person-minutes. Unpaid entries are already left out. */
  minutes: number;
  /**
   * Their entries marked unpaid, one still open at the scheduled length.
   * They cost nothing, but they are still time worked on the visit, set
   * against the expected hours.
   */
  unpaidMinutes: number;
  minutesSource: MinutesSource;
  /** They clocked in, and every entry is marked unpaid. */
  allUnpaid: boolean;
  /**
   * Their time is not really known: an entry still open, or nothing punched
   * on a visit the rest of the crew clocked. Their minutes are the schedule's.
   */
  timeIncomplete: boolean;
}

/** The company's rule for the owner's own time. Off by default. */
export interface OwnerCostSettings {
  enabled: boolean;
  hourlyCents: number | null;
}

/**
 * How a line was costed.
 *
 *   * `costed` — at their rate (or the owner's rate).
 *   * `pay_missing` — no worker type, or no rate in force on the date. The
 *     visit cannot say what it made until somebody sets it.
 *   * `owner_free` — the owner, whose time the company has not chosen to cost.
 *   * `unpaid` — every entry they clocked was marked unpaid.
 */
export type WorkerLineStatus = 'costed' | 'pay_missing' | 'owner_free' | 'unpaid';

export interface WorkerCostLine {
  userId: string;
  name: string;
  workerType: WorkerType | null;
  /** `owner` for an owner costed by the company setting. */
  payType: PayType | 'owner' | null;
  /** The paid minutes the line was costed on. */
  minutes: number;
  /** Time worked unpaid: no cost, but on the visit all the same. */
  unpaidMinutes: number;
  minutesSource: MinutesSource;
  /** Pay before the load. */
  baseCents: number;
  /** The W-2 load on that pay. */
  burdenCents: number;
  burdenBasisPoints: number;
  status: WorkerLineStatus;
  /** A salary's hourly equivalent — never exact. */
  approximate: boolean;
  /** Hourly cost on minutes nobody recorded. */
  timeIncomplete: boolean;
}

/**
 * One worker's cost on one job.
 *
 * `jobBasisCents` is what a percentage is taken of: the price after
 * discounts and before tax, tips and refunds — a refund later takes revenue
 * back from the company, not wages back from the crew.
 */
export function costWorker(worker: WorkerOnVisit, jobBasisCents: number, owner: OwnerCostSettings): WorkerCostLine {
  const line: WorkerCostLine = {
    userId: worker.userId,
    name: worker.name,
    workerType: worker.workerType,
    payType: null,
    minutes: worker.minutes,
    unpaidMinutes: worker.unpaidMinutes,
    minutesSource: worker.minutesSource,
    baseCents: 0,
    burdenCents: 0,
    burdenBasisPoints: 0,
    status: 'costed',
    approximate: false,
    timeIncomplete: false,
  };

  if (worker.isOwner) {
    if (!owner.enabled || owner.hourlyCents === null) return { ...line, status: 'owner_free' };
    if (worker.allUnpaid) return { ...line, payType: 'owner', status: 'unpaid' };
    return {
      ...line,
      payType: 'owner',
      baseCents: centsForMinutes(worker.minutes, owner.hourlyCents),
      timeIncomplete: worker.timeIncomplete,
    };
  }

  const rate = worker.rate;
  // Time nobody is paid for costs nothing whatever the person's terms: a
  // trainee riding along needs no pay set to be costed at zero.
  if (worker.allUnpaid) return { ...line, payType: rate?.payType ?? null, status: 'unpaid' };
  if (worker.workerType === null || rate === null)
    return { ...line, payType: rate?.payType ?? null, status: 'pay_missing' };

  let baseCents: number;
  let approximate = false;
  let timeIncomplete = false;
  switch (rate.payType) {
    case 'hourly':
      if (rate.rateCents === null) return { ...line, payType: rate.payType, status: 'pay_missing' };
      baseCents = centsForMinutes(worker.minutes, rate.rateCents);
      timeIncomplete = worker.timeIncomplete;
      break;
    case 'salary':
      if (rate.annualSalaryCents === null) return { ...line, payType: rate.payType, status: 'pay_missing' };
      baseCents = centsForMinutes(worker.minutes, salaryHourlyCents(rate.annualSalaryCents, rate.salaryHoursPerYear));
      // Approximate by nature, so scheduled hours on a salary are the same
      // kind of figure rather than a hole in the record.
      approximate = true;
      break;
    case 'per_job':
      if (rate.rateCents === null) return { ...line, payType: rate.payType, status: 'pay_missing' };
      baseCents = rate.rateCents;
      break;
    case 'percent':
      if (rate.rateBasisPoints === null) return { ...line, payType: rate.payType, status: 'pay_missing' };
      baseCents = applyBasisPoints(Math.max(0, jobBasisCents), rate.rateBasisPoints);
      break;
  }

  const burdenBasisPoints = worker.workerType === 'w2' ? Math.max(0, worker.burdenBasisPoints) : 0;
  return {
    ...line,
    payType: rate.payType,
    baseCents,
    burdenBasisPoints,
    burdenCents: applyBasisPoints(baseCents, burdenBasisPoints),
    approximate,
    timeIncomplete,
  };
}

// ---------------------------------------------------------------------------
// Revenue
// ---------------------------------------------------------------------------

/**
 * The tax inside a tax-inclusive price, as the quote it came from computed
 * it: the quote's tax over the total it was sold at. Null when the price
 * carries no tax (no quote, or a quote with tax off).
 */
export interface TaxShare {
  taxCents: number;
  totalCents: number;
}

/**
 * A quote's tax share. A manual total keeps the calculated tax and moves only
 * the rest of the price, so the tax is a share of the total the quote was
 * sold at, never of the total it was calculated at. Null when the quote
 * carries no tax, or a total too small to hold it.
 */
export function quoteTaxShare(taxCents: number | null, soldTotalCents: number | null): TaxShare | null {
  const tax = taxCents ?? 0;
  const total = soldTotalCents ?? 0;
  return tax > 0 && total > tax ? { taxCents: tax, totalCents: total } : null;
}

/** A tax-inclusive amount with its tax taken back out. */
export function withoutTax(amountCents: number, share: TaxShare | null): number {
  if (share === null || share.taxCents <= 0 || share.totalCents <= 0 || share.taxCents >= share.totalCents) {
    return amountCents;
  }
  return amountCents - roundHalfUp((amountCents * share.taxCents) / share.totalCents);
}

/** The payment rows profit reads: a visit's own ledger. */
export interface ProfitLedgerRow {
  id: string;
  type: 'charge' | 'refund';
  status: string;
  method: PaymentMethodKind | string;
  amountCents: number;
  tipCents: number;
  cardFeeCents: number;
  refundOfPaymentId: string | null;
}

/** Card payments taken through the provider — the ones that cost a processing fee. */
export const CARD_PAYMENT_METHODS: readonly string[] = ['card_on_file', 'checkout_link', 'tap_to_pay'];

/**
 * How much of the work's price has gone back to the customer: each succeeded
 * refund against a charge, capped at that charge's principal (its amount less
 * tip and surcharge — a refund beyond that is the tip going home, which was
 * never revenue). The same cap the collected-money arithmetic applies.
 */
export function refundedPrincipalCents(rows: readonly ProfitLedgerRow[]): number {
  const principalByCharge = new Map<string, number>();
  for (const row of rows) {
    if (row.type === 'charge' && row.status === 'succeeded') {
      principalByCharge.set(row.id, Math.max(0, row.amountCents - row.tipCents - row.cardFeeCents));
    }
  }
  const refundedByCharge = new Map<string, number>();
  let unlinked = 0;
  for (const row of rows) {
    if (row.type !== 'refund' || row.status !== 'succeeded') continue;
    if (row.refundOfPaymentId !== null && principalByCharge.has(row.refundOfPaymentId)) {
      refundedByCharge.set(row.refundOfPaymentId, (refundedByCharge.get(row.refundOfPaymentId) ?? 0) + row.amountCents);
    } else {
      unlinked += row.amountCents;
    }
  }
  let refunded = unlinked;
  for (const [chargeId, amount] of refundedByCharge) {
    refunded += Math.min(amount, principalByCharge.get(chargeId) ?? 0);
  }
  return refunded;
}

/**
 * A visit's revenue and the basis its percentages are taken of.
 *
 * `jobBasisCents` — the price after discounts, without tax.
 * `revenueCents` — that, less the pre-tax part of what was refunded, never
 * below zero (a refund of more than the work cost is not negative revenue).
 */
export function visitRevenue(input: {
  priceCents: number;
  taxShare: TaxShare | null;
  refundedPrincipalCents: number;
}): {
  jobBasisCents: number;
  revenueCents: number;
} {
  const priceCents = Math.max(0, input.priceCents);
  const jobBasisCents = withoutTax(priceCents, input.taxShare);
  const refunded = withoutTax(Math.min(Math.max(0, input.refundedPrincipalCents), priceCents), input.taxShare);
  return { jobBasisCents, revenueCents: Math.max(0, jobBasisCents - refunded) };
}

/**
 * The sales tax a visit still collected after refunds: the tax inside its
 * price, in the share of the pre-tax price the company kept. A refund gives
 * back its part of the tax with it, the same proportion `visitRevenue` takes
 * off the revenue.
 */
export function salesTaxKeptCents(priceCents: number, taxShare: TaxShare | null, revenueCents: number): number {
  const price = Math.max(0, priceCents);
  const basis = withoutTax(price, taxShare);
  const tax = price - basis;
  if (tax <= 0 || basis <= 0) return 0;
  return roundHalfUp((tax * Math.min(Math.max(0, revenueCents), basis)) / basis);
}

// ---------------------------------------------------------------------------
// Card fees
// ---------------------------------------------------------------------------

/** What a card payment costs the company: a share of the charge plus a fixed amount. */
export interface CardProcessingRate {
  basisPoints: number;
  fixedCents: number;
}

/** The processing cost of one card charge, net of the surcharge the customer paid. */
export function netCardFeeCents(amountCents: number, surchargeCents: number, rate: CardProcessingRate): number {
  const processing = applyBasisPoints(Math.max(0, amountCents), rate.basisPoints) + rate.fixedCents;
  return Math.max(0, processing - Math.max(0, surchargeCents));
}

/**
 * The card fees on a visit's ledger, estimated per succeeded card charge.
 *
 * Estimated because the processor's own fee is not recorded on a payment —
 * `card_fee_cents` is the surcharge the customer paid on top, which is why it
 * is subtracted here: a fee passed on to the customer nets out. A refund does
 * not give the processing back (the processor keeps it), but one that goes
 * past the charge's principal hands the surcharge back too — the same order
 * `refundedPrincipalCents` reads a refund in — and the part handed back no
 * longer offsets the fee.
 */
export function estimateCardFeesCents(rows: readonly ProfitLedgerRow[], rate: CardProcessingRate): number {
  const refundedByCharge = new Map<string, number>();
  for (const row of rows) {
    if (row.type !== 'refund' || row.status !== 'succeeded' || row.refundOfPaymentId === null) continue;
    refundedByCharge.set(row.refundOfPaymentId, (refundedByCharge.get(row.refundOfPaymentId) ?? 0) + row.amountCents);
  }
  let total = 0;
  for (const row of rows) {
    if (row.type !== 'charge' || row.status !== 'succeeded') continue;
    if (!CARD_PAYMENT_METHODS.includes(row.method)) continue;
    const principal = Math.max(0, row.amountCents - row.tipCents - row.cardFeeCents);
    const pastPrincipal = Math.max(0, (refundedByCharge.get(row.id) ?? 0) - principal);
    const surchargeKept = Math.max(0, row.cardFeeCents - pastPrincipal);
    total += netCardFeeCents(row.amountCents, surchargeKept, rate);
  }
  return total;
}

/**
 * The card fee a job paid by card is expected to cost: the processing on the
 * price and any surcharge, less the surcharge itself.
 */
export function expectedCardFeeCents(
  priceCents: number,
  rate: CardProcessingRate,
  surchargeBasisPoints: number,
): number {
  const surcharge = applyBasisPoints(Math.max(0, priceCents), Math.max(0, surchargeBasisPoints));
  return netCardFeeCents(priceCents + surcharge, surcharge, rate);
}

// ---------------------------------------------------------------------------
// One visit
// ---------------------------------------------------------------------------

export interface VisitProfitInput {
  revenueCents: number;
  /** What percentages are taken of: after discounts, before tax and refunds. */
  jobBasisCents: number;
  workers: readonly WorkerOnVisit[];
  owner: OwnerCostSettings;
  feeCents: number;
  /** Supplies as a share of the price; null or zero leaves them out. */
  suppliesBasisPoints: number | null;
}

export interface VisitProfit {
  revenueCents: number;
  /** Pay before the load, summed over the crew. */
  laborCents: number;
  /** The W-2 load on that pay. */
  burdenCents: number;
  feeCents: number;
  suppliesCents: number;
  profitCents: number;
  /** Profit ÷ revenue; null when there was no revenue to divide. */
  marginBasisPoints: number | null;
  /**
   * Person-minutes worked on the visit, paid or not: the hours set against
   * the expected ones. What anybody is paid for is on each line.
   */
  laborMinutes: number;
  lines: WorkerCostLine[];
  /** Somebody on the visit has no worker type or no rate for its date. */
  payMissing: boolean;
  /** Somebody paid by the hour has no recorded time. */
  timeIncomplete: boolean;
  /** A salary's hourly equivalent is in the figure. */
  approximate: boolean;
}

/** Profit as a share of revenue, or null when there is none. */
export function marginBasisPoints(profitCents: number, revenueCents: number): number | null {
  if (revenueCents <= 0) return null;
  return roundHalfUp((profitCents * BASIS_POINTS_SCALE) / revenueCents);
}

export function computeVisitProfit(input: VisitProfitInput): VisitProfit {
  const lines = input.workers.map((worker) => costWorker(worker, input.jobBasisCents, input.owner));
  const laborCents = lines.reduce((sum, line) => sum + line.baseCents, 0);
  const burdenCents = lines.reduce((sum, line) => sum + line.burdenCents, 0);
  const suppliesCents =
    input.suppliesBasisPoints !== null && input.suppliesBasisPoints > 0
      ? applyBasisPoints(Math.max(0, input.jobBasisCents), input.suppliesBasisPoints)
      : 0;
  const feeCents = Math.max(0, input.feeCents);
  const profitCents = input.revenueCents - laborCents - burdenCents - feeCents - suppliesCents;
  return {
    revenueCents: input.revenueCents,
    laborCents,
    burdenCents,
    feeCents,
    suppliesCents,
    profitCents,
    marginBasisPoints: marginBasisPoints(profitCents, input.revenueCents),
    laborMinutes: lines.reduce((sum, line) => sum + line.minutes + line.unpaidMinutes, 0),
    lines,
    payMissing: lines.some((line) => line.status === 'pay_missing'),
    timeIncomplete: lines.some((line) => line.timeIncomplete),
    approximate: lines.some((line) => line.approximate),
  };
}

// ---------------------------------------------------------------------------
// Estimates (an open visit, a quote)
// ---------------------------------------------------------------------------

/** What a job is expected to cost, split by whether the cost moves with the price. */
export interface JobCostEstimate {
  laborCents: number;
  burdenCents: number;
  feeCents: number;
  suppliesCents: number;
  /** Labor, load, fees and supplies. */
  totalCostCents: number;
  /**
   * The part of the cost that does not depend on the price — hours, per-job
   * amounts and the card fee's fixed part — for working out a price that
   * reaches a target margin.
   */
  fixedCostCents: number;
  /** The part that does: percent pay with its load, supplies, the card fee's share. */
  variableBasisPoints: number;
  /**
   * The known crew's own lines, each for their slot on every visit — the
   * same amounts `laborCents` and `burdenCents` add up.
   */
  lines: WorkerCostLine[];
  /** Slots nobody in particular is known for, costed at the team average. */
  openSlots: number;
  /** A crew member with no pay recorded — the estimate leaves them out. */
  payMissing: boolean;
  approximate: boolean;
}

export interface JobEstimateInput {
  /** The price the job is sold at, after discounts, without tax. */
  jobBasisCents: number;
  /** Person-minutes of work the job is expected to take, the whole crew together. */
  laborMinutes: number;
  /** How many people share those minutes. */
  crewSize: number;
  /**
   * How many visits the price and minutes cover — one, or a month of them on
   * a monthly price. Per-job pay is paid on every visit; one card payment
   * still covers the lot. Defaults to 1.
   */
  visits?: number;
}

/**
 * The visits a month holds at `visitsPerWeek` — the count a monthly quote is
 * priced on: one to seven a week, 52 weeks, over twelve months.
 */
export function visitsPerMonth(visitsPerWeek: number | null | undefined): number {
  const perWeek = Math.min(7, Math.max(1, Math.trunc(visitsPerWeek ?? 1)));
  return (perWeek * 52) / 12;
}

export interface CrewCostInputs {
  /**
   * The people expected on the job, one per crew slot when known; empty means
   * the crew is not known and `team` stands in.
   */
  crew: readonly WorkerPayTerms[];
  /** Everyone costable, for the team-average fallback. */
  team: readonly WorkerPayTerms[];
  owner: OwnerCostSettings;
  suppliesBasisPoints: number | null;
  /** Null when the customer does not usually pay by card. */
  cardFee: (CardProcessingRate & { surchargeBasisPoints: number }) | null;
}

/** One person's cost for one crew slot of a job. */
function slotCost(
  terms: WorkerPayTerms,
  minutes: number,
  jobBasisCents: number,
  owner: OwnerCostSettings,
): { line: WorkerCostLine; variableBasisPoints: number } {
  const line = costWorker(
    { ...terms, minutes, unpaidMinutes: 0, minutesSource: 'scheduled', allUnpaid: false, timeIncomplete: false },
    jobBasisCents,
    owner,
  );
  // Percent pay grows with the price, load included.
  const percent = line.status === 'costed' && line.payType === 'percent' ? (terms.rate?.rateBasisPoints ?? 0) : 0;
  const variableBasisPoints = roundHalfUp(
    (percent * (BASIS_POINTS_SCALE + line.burdenBasisPoints)) / BASIS_POINTS_SCALE,
  );
  return { line, variableBasisPoints };
}

/**
 * The expected cost of a job before it is done.
 *
 * The job has `crewSize` slots, each an even share of the minutes. The known
 * crew fill them first, each costed at their own terms; a slot nobody in
 * particular is known for is costed at the average of what the team's
 * costable members would cost in it — the "company average".
 */
export function estimateJobCost(job: JobEstimateInput, inputs: CrewCostInputs): JobCostEstimate {
  const crewSize = Math.max(1, Math.round(job.crewSize));
  // Each slot is costed for one visit — its share of the minutes and of the
  // price — and then for every visit the price covers.
  const visits = Math.max(1, job.visits ?? 1);
  const slotMinutes = job.laborMinutes / crewSize / visits;
  const visitBasisCents = job.jobBasisCents / visits;
  const known = inputs.crew.slice(0, crewSize);
  const lines: WorkerCostLine[] = [];
  let laborCents = 0;
  let burdenCents = 0;
  let percentBasisPoints = 0;
  let payMissing = false;
  let approximate = false;

  for (const member of known) {
    const { line, variableBasisPoints } = slotCost(member, slotMinutes, visitBasisCents, inputs.owner);
    const slot = {
      ...line,
      minutes: roundHalfUp(line.minutes * visits),
      baseCents: roundHalfUp(line.baseCents * visits),
      burdenCents: roundHalfUp(line.burdenCents * visits),
    };
    lines.push(slot);
    laborCents += slot.baseCents;
    burdenCents += slot.burdenCents;
    percentBasisPoints += variableBasisPoints;
    payMissing ||= line.status === 'pay_missing';
    approximate ||= line.approximate;
  }

  const openSlots = crewSize - known.length;
  if (openSlots > 0) {
    const costed = inputs.team
      .map((member) => slotCost(member, slotMinutes, visitBasisCents, inputs.owner))
      .filter(({ line }) => line.status === 'costed');
    if (costed.length === 0) {
      payMissing = true;
    } else {
      const mean = (pick: (entry: (typeof costed)[number]) => number) =>
        costed.reduce((sum, entry) => sum + pick(entry), 0) / costed.length;
      laborCents += roundHalfUp(mean((entry) => entry.line.baseCents) * openSlots * visits);
      burdenCents += roundHalfUp(mean((entry) => entry.line.burdenCents) * openSlots * visits);
      percentBasisPoints += roundHalfUp(mean((entry) => entry.variableBasisPoints) * openSlots);
      approximate ||= costed.some(({ line }) => line.approximate);
    }
  }

  const suppliesBasisPoints =
    inputs.suppliesBasisPoints !== null && inputs.suppliesBasisPoints > 0 ? inputs.suppliesBasisPoints : 0;
  const suppliesCents = applyBasisPoints(Math.max(0, job.jobBasisCents), suppliesBasisPoints);
  const feeCents = inputs.cardFee
    ? expectedCardFeeCents(job.jobBasisCents, inputs.cardFee, inputs.cardFee.surchargeBasisPoints)
    : 0;

  // The fee's moving part. Processing is charged on the price and the
  // surcharge together, so its share of the price is the rate plus the rate
  // of the surcharge (rounded up, so a target price errs high); the surcharge
  // itself, which the customer pays, comes off. A surcharge at or above that
  // leaves the fee all fixed.
  let feeVariableBasisPoints = 0;
  if (inputs.cardFee) {
    const surcharge = Math.max(0, inputs.cardFee.surchargeBasisPoints);
    const onSurcharge = Math.ceil((inputs.cardFee.basisPoints * surcharge) / BASIS_POINTS_SCALE);
    feeVariableBasisPoints = Math.max(0, inputs.cardFee.basisPoints + onSurcharge - surcharge);
  }
  const variableBasisPoints = percentBasisPoints + suppliesBasisPoints + feeVariableBasisPoints;
  const totalCostCents = laborCents + burdenCents + feeCents + suppliesCents;
  const variableCents = applyBasisPoints(Math.max(0, job.jobBasisCents), variableBasisPoints);

  return {
    laborCents,
    burdenCents,
    feeCents,
    suppliesCents,
    totalCostCents,
    fixedCostCents: Math.max(0, totalCostCents - variableCents),
    variableBasisPoints,
    lines,
    openSlots,
    payMissing,
    approximate,
  };
}

/**
 * The lowest price, without tax, that reaches `targetBasisPoints` of margin
 * for this cost shape: price − (fixed + price × variable) = price × target.
 * Rounded up to a whole currency unit. Null when no price can reach it,
 * because the costs that grow with the price already eat the target.
 */
export function priceForTargetMargin(estimate: JobCostEstimate, targetBasisPoints: number): number | null {
  const remaining = BASIS_POINTS_SCALE - estimate.variableBasisPoints - targetBasisPoints;
  if (remaining <= 0) return null;
  const exact = (estimate.fixedCostCents * BASIS_POINTS_SCALE) / remaining;
  return Math.ceil(exact / 100) * 100;
}

/** A tax-exclusive price grossed up to what the customer pays, at a quote's tax share. */
export function withTax(amountCents: number, share: TaxShare | null): number {
  if (share === null || share.taxCents <= 0 || share.totalCents <= share.taxCents) return amountCents;
  return roundHalfUp((amountCents * share.totalCents) / (share.totalCents - share.taxCents));
}
